<script setup lang="ts">
import { computed, ref, watch, onBeforeMount } from "vue";

import CheckBox from "@/components/Reusables/Inputs/CheckBox.vue";
import { SpeckleMaterial } from "@/types/Views/MappingWizard/SpeckleMaterial";

import NumberInput from "@/components/Reusables/Inputs/NumberInput.vue";
import { formatNumber } from "@/services/Utils/DecimalFormatter";
import { useEnum } from "@/stores/project/project.enum";
import { useProjectEditReadOnly } from "@/pages/ProjectVersionHistory/composables/useProjectEditReadOnly.composable";

const enumStore = useEnum();
const { declaredUnits } = enumStore;

// The Type-grouping twin of MaterialMappingForm, and gated the same way: the save this feeds is
// refused for a Viewer and while a frozen revision is previewed, so the fields go inert rather than
// inviting an edit that can never be committed (RTLCA-5582).
const { isReadOnly, readOnlyTooltip } = useProjectEditReadOnly("top");

const canEdit = computed(() => !isReadOnly.value);

const props = defineProps<{
  material: SpeckleMaterial;
  materialCount: number;
  index: number;
  library: any;
}>();

const emits = defineEmits<{
  (e: "isMaterialValid", value: boolean, index: number): void;
}>();

const applyLifetimeToAll = ref<boolean>(false);

// Getter and setter for unit newValue
const getUnitNewValue = (unit: any) => {
  return computed({
    get: () => unit.newValue ?? null,
    set: (value: number | null) => {
      unit.newValue = value;
    },
  });
};

const addNewUnit = ref(false);
const showUnitTable = ref(true);

onBeforeMount(() => {
  const material = props.material;

  material.lifeTime = props.library.expectedLifespan;

  const libraryDeclaredUnit = props.library.declaredUnit?.declaredUnit;

  // Find existing unit with matching declaredUnit
  const existingUnit = material.totalDeclaredUnits?.find((unit: any) => unit.declaredUnit === libraryDeclaredUnit);

  if (existingUnit) {
    // Always ensure the library's declared unit is preferred
    material.totalDeclaredUnits?.forEach((unit: any) => {
      unit.preferred = unit.declaredUnit === libraryDeclaredUnit;
    });
  } else {
    addNewUnit.value = true;
    material.totalDeclaredUnits?.forEach((unit: any) => {
      unit.preferred = false;
    });
    const newUnit: any = {
      declaredUnit: props.library.declaredUnit.declaredUnit,
      preferred: true,
      value: 0,
      newValue: null,
      source: 0,
      wasChanged: false,
      isNew: true,
    };
    material.totalDeclaredUnits.push(newUnit);
  }

  showUnitTable.value = !materialValidUnit.value;
});

const materialValidUnit = computed(() => {
  const material = props.material;
  const units = material.totalDeclaredUnits ?? [];
  const libraryDeclaredUnit = props.library.declaredUnit?.declaredUnit;

  // Helper: does a unit have a positive value?
  const unitHasValue = (u: any | undefined) => {
    if (!u) return false;
    const v = u.newValue ?? u.value;
    return v != null && Number(v) > 0;
  };

  // Unit matching the library's declared unit (we don't care if it's new or not here)
  const matchingUnit = units.find((u: any) => u.declaredUnit === libraryDeclaredUnit);

  // Preferred unit (if any)
  const preferredUnit = units.find((u: any) => u.preferred);

  // ✅ valid if either matchingUnit OR preferredUnit has a positive value
  return unitHasValue(matchingUnit) || unitHasValue(preferredUnit);
});

const materialValidityMap = computed(() => {
  const material = props.material;

  // ✅ valid if either matchingUnit OR preferredUnit has a positive value
  const hasValidUnitValue = materialValidUnit.value;

  // Lifetime logic
  let lifetimeValue = material.lifeTime ?? material.mapping?.expectedLifespan;
  // You can simplify this since cloneMaterial/currentMaterialIndex are gone
  if (applyLifetimeToAll.value && material.lifeTime != null) {
    lifetimeValue = material.lifeTime;
  }

  const hasValidLifetime = lifetimeValue != null && Number(lifetimeValue) > 0;

  const isValid = hasValidLifetime && hasValidUnitValue;

  return isValid;
});

watch(
  materialValidityMap,
  (isValid) => {
    emits("isMaterialValid", isValid, props.index);
  },
  { immediate: true }
);
</script>

<template>
  <div
    v-if="showUnitTable"
    class="flex flex-col">
    <div class="rounded-4 mb-6 flex gap-6 border px-3 pb-3.5">
      <div class="flex w-full flex-col">
        <div
          class="sticky z-2 bg-slate-100 pt-3 pr-2 pb-2"
          style="top: -36px">
          <!-- style kept: negative 36px offset not expressible with standard Tailwind spacing tokens -->
          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <p class="t-sh4 pb-1">{{ material.identifier }}</p>
            </div>
          </div>
        </div>

        <div class="text-text-color min-w-[500px] pt-4 text-sm">
          <div class="border-element-stroke rounded-16 grid grid-cols-12 rounded-b-none border bg-slate-100 p-2 font-semibold">
            <span class="col-span-2 pl-2">Preferred</span>
            <span class="col-span-3">Unit</span>
            <span class="col-span-3">Value</span>
            <span class="col-span-4"> Assign new value </span>
          </div>

          <div
            v-for="unit in material.totalDeclaredUnits"
            :key="unit.declaredUnit"
            class="border-element-stroke text-type-muted rounded-16 rounded-t-none rounded-b-none border-x border-b last:rounded-b">
            <div
              v-if="unit.preferred"
              class="grid grid-cols-12 items-center px-4 py-2">
              <span class="col-span-2">
                <CheckBox
                  custom-class="rounded-full!"
                  color="input"
                  size="lg"
                  :disabled="!canEdit"
                  :value="unit.preferred" />
              </span>
              <span class="col-span-3">
                {{ declaredUnits[unit.declaredUnit].DisplayName }}
              </span>
              <span class="col-span-3">{{ formatNumber(unit.value ?? 0) }}</span>

              <div
                v-tooltip="readOnlyTooltip"
                class="col-span-4">
                <NumberInput
                  color="primary"
                  placeholder="new value"
                  :value="getUnitNewValue(unit).value"
                  :max-decimals="3"
                  :disabled="!canEdit"
                  :error-msg="unit.value === 0 && (unit.newValue === 0 || unit.newValue == undefined) ? 'Missing value' : ''"
                  class="w-4/5"
                  @update:value="getUnitNewValue(unit).value = $event" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
